<template>
  <div class="simulator-card">
    <div class="header">
      <h1>🍅 番茄生长实验室 <span>环境 + 偶然灾害</span></h1>
      <div class="sub">⚙️ 调整环境因素，观察生长状态 —— 但即便条件完美，也可能遭遇意外（倒伏/虫害）</div>
    </div>

    <div class="dashboard">
      <!-- 左侧控制区 -->
      <div class="factors-panel">
        <h2>🌿 环境因素</h2>
        <div class="factor-grid">
          <div class="factor-item">
            <div class="factor-label"><span class="icon-em">💨</span> 有无空气</div>
            <div style="display: flex; align-items: center; gap: 10px;">
              <label class="toggle-switch">
                <input type="checkbox" v-model="hasAir">
                <span class="slider"></span>
              </label>
              <span class="factor-status-text">{{ hasAir ? '有' : '无' }}</span>
            </div>
          </div>

          <div class="factor-item">
            <div class="factor-label"><span class="icon-em">💧</span> 有无水分</div>
            <div style="display: flex; align-items: center; gap: 10px;">
              <label class="toggle-switch">
                <input type="checkbox" v-model="hasWater">
                <span class="slider"></span>
              </label>
              <span class="factor-status-text">{{ hasWater ? '有' : '无' }}</span>
            </div>
          </div>

          <div class="factor-item">
            <div class="factor-label"><span class="icon-em">🌡️</span> 适宜的温度</div>
            <div style="display: flex; align-items: center; gap: 10px;">
              <label class="toggle-switch">
                <input type="checkbox" v-model="tempSuitable">
                <span class="slider"></span>
              </label>
              <span class="factor-status-text">{{ tempSuitable ? '适宜' : '不适宜' }}</span>
            </div>
          </div>

          <div class="factor-item">
            <div class="factor-label"><span class="icon-em">☀️</span> 有无光照</div>
            <div style="display: flex; align-items: center; gap: 10px;">
              <label class="toggle-switch">
                <input type="checkbox" v-model="hasLight">
                <span class="slider"></span>
              </label>
              <span class="factor-status-text">{{ hasLight ? '有' : '无' }}</span>
            </div>
          </div>

          <div class="factor-item">
            <div class="factor-label"><span class="icon-em">🪴</span> 有无土壤</div>
            <div style="display: flex; align-items: center; gap: 10px;">
              <label class="toggle-switch">
                <input type="checkbox" v-model="hasSoil">
                <span class="slider"></span>
              </label>
              <span class="factor-status-text">{{ hasSoil ? '有' : '无' }}</span>
            </div>
          </div>
        </div>

        <div class="quick-buttons">
          <button class="quick-btn" @click="setAllChecks(true)">✅ 全选 (理想环境)</button>
          <button class="quick-btn" @click="setAllChecks(false)">❌ 全不选 (极端恶劣)</button>
          <button class="quick-btn reset-all" @click="resetToDefault">🔄 重置默认 (全优)</button>
          <button class="quick-btn" @click="toClose" style="background:#ff5252;color:#fff;">← 返回</button>
        </div>

        <div class="condition-hint" style="margin-top: 20px; background: #f1e7cf;">
          💡 探索提示：<br>
          • 空气+水分+温度 → 发芽必需。<br>
          • 再加上光照+土壤 → 具备长大条件。<br>
          • ⚠️ 但即便环境完美，仍可能随机发生：<strong style="color:#b34e1a;">倒伏(60%)或虫害(20%)</strong>，引导思考“所有条件满足就一定能成功吗？”<br>
          • 每次调整环境开关都会重新计算偶然事件。
        </div>
      </div>

      <!-- 右侧生长展示 -->
      <div class="growth-panel">
        <div class="growth-title">🍅 番茄生长实录</div>
        <div class="illustration">
          <div v-show="finalState === 'noGerm'" class="svg-container">
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <rect x="15" y="60" width="70" height="25" rx="10" fill="#8B5A2B" stroke="#5a3e1f" stroke-width="1.5"></rect>
              <circle cx="50" cy="55" r="12" fill="#B97F44" stroke="#6B3E1A" stroke-width="1.2"></circle>
              <circle cx="46" cy="52" r="2" fill="#4A2A0E"></circle>
              <circle cx="54" cy="52" r="2" fill="#4A2A0E"></circle>
              <path d="M48 58 L52 58 L50 62 Z" fill="#6B3E1A"></path>
              <text x="50" y="92" font-size="8" text-anchor="middle" fill="#4c2f18" font-weight="bold">⚡ 种子沉睡</text>
            </svg>
            <div style="margin-top: 10px; font-weight: bold; color:#8B5A2B;">🌰 不发芽</div>
          </div>

          <div v-show="finalState === 'sprout'" class="svg-container">
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <rect x="25" y="65" width="50" height="18" rx="6" fill="#A57C4C" stroke="#6B3E1A" stroke-width="1"></rect>
              <path d="M50 65 L50 35" stroke="#3c9e3c" stroke-width="3.5" fill="none" stroke-linecap="round"></path>
              <path d="M50 48 Q38 40 32 48" stroke="#58c458" stroke-width="3" fill="none" stroke-linecap="round"></path>
              <path d="M50 48 Q62 40 68 48" stroke="#58c458" stroke-width="3" fill="none" stroke-linecap="round"></path>
              <circle cx="50" cy="32" r="3" fill="#c9e64a"></circle>
              <text x="50" y="92" font-size="8" text-anchor="middle" fill="#2c6e2c" font-weight="bold">🌱 幼芽待哺</text>
            </svg>
            <div style="margin-top: 10px; font-weight: bold; color:#2e7d32;">🌿 发芽但不生长</div>
          </div>

          <div v-show="finalState === 'grown'" class="svg-container">
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <rect x="30" y="68" width="40" height="14" rx="5" fill="#8B5A2B" stroke="#5a3e1f" stroke-width="1"></rect>
              <path d="M50 68 L50 25" stroke="#2c6e2c" stroke-width="4" fill="none" stroke-linecap="round"></path>
              <path d="M50 45 Q35 35 28 46" stroke="#3da83d" stroke-width="3" fill="none" stroke-linecap="round"></path>
              <path d="M50 45 Q65 35 72 46" stroke="#3da83d" stroke-width="3" fill="none" stroke-linecap="round"></path>
              <path d="M50 58 Q38 50 32 58" stroke="#3da83d" stroke-width="2.5" fill="none" stroke-linecap="round"></path>
              <circle cx="52" cy="30" r="9" fill="#e63e2e" stroke="#a12216" stroke-width="1"></circle>
              <path d="M48 28 L56 28 M52 24 L52 32" stroke="#f5e56b" stroke-width="1.2"></path>
              <text x="50" y="92" font-size="8" text-anchor="middle" fill="#aa2e1e" font-weight="bold">🍅 硕果累累</text>
            </svg>
            <div style="margin-top: 10px; font-weight: bold; color:#bf5e2c;">🍅 成功长大</div>
          </div>

          <div v-show="finalState === 'lodging'" class="svg-container">
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <rect x="35" y="70" width="30" height="12" rx="4" fill="#8B5A2B" stroke="#5a3e1f" stroke-width="1"></rect>
              <path d="M50 70 L42 40" stroke="#6b4c2c" stroke-width="4" fill="none" stroke-linecap="round" transform="rotate(25,50,70)"></path>
              <path d="M48 48 Q35 42 30 50" stroke="#5b8c4e" stroke-width="2.5" fill="none"></path>
              <circle cx="40" cy="38" r="7" fill="#e63e2e" stroke="#a12216" stroke-width="1" transform="rotate(-15,40,38)"></circle>
              <text x="50" y="92" font-size="8" text-anchor="middle" fill="#8b3c1a" font-weight="bold">🌬️ 严重倒伏</text>
            </svg>
            <div style="margin-top: 10px; font-weight: bold; color:#b4511b;">🥀 倒伏灾害</div>
          </div>

          <div v-show="finalState === 'pest'" class="svg-container">
            <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
              <rect x="30" y="68" width="40" height="12" rx="5" fill="#8B5A2B" stroke="#5a3e1f" stroke-width="1"></rect>
              <path d="M50 68 L50 35" stroke="#2c6e2c" stroke-width="3.5" fill="none" stroke-dasharray="3 2"></path>
              <path d="M50 45 Q35 38 30 46" stroke="#7a9e4e" stroke-width="2.5" fill="none"></path>
              <circle cx="45" cy="30" r="3" fill="#e6c35c"></circle>
              <circle cx="60" cy="38" r="4" fill="#5a3e1a" stroke="#2f1f0a" stroke-width="1"></circle>
              <text x="50" y="92" font-size="8" text-anchor="middle" fill="#8b5a2b" font-weight="bold">🐛 虫害侵袭</text>
            </svg>
            <div style="margin-top: 10px; font-weight: bold; color:#ad6b35;">🐛 虫害灾害</div>
          </div>
        </div>

        <div class="state-message">
          <div class="state-name">{{ stateTitle }}</div>
          <div class="state-detail">{{ stateDesc }}</div>
        </div>

        <div class="condition-hint" v-html="analysisHint"></div>
        <div class="warning-badge" v-html="randomEventNote"></div>
      </div>
    </div>

    <footer>
      🌟 探究任务：是不是只要空气、水、温度、光照、土壤都齐全，番茄就一定能健康长大？<br>
      🌧️ 倒伏(60%) 和 虫害(20%) 展示了自然条件下不可控的风险，引导学生思考农业中的真实挑战。
    </footer>
  </div>
</template>

<script>
export default {
  name: "TomatoSimulator",
  data() {
    return {
      hasAir: true,
      hasWater: true,
      tempSuitable: true,
      hasLight: true,
      hasSoil: true,

      finalState: "lodging",
      stateTitle: "",
      stateDesc: "",
      analysisHint: "",
      randomEventNote: "",
    };
  },
  watch: {
    hasAir() { this.updateUI(); },
    hasWater() { this.updateUI(); },
    tempSuitable() { this.updateUI(); },
    hasLight() { this.updateUI(); },
    hasSoil() { this.updateUI(); },
  },
  mounted() {
    this.updateUI();
  },
  methods: {
    toClose() {
      this.$emit("toBack");
    },
    getBaseGrowthState() {
      const germinationPossible = this.hasAir && this.hasWater && this.tempSuitable;
      if (!germinationPossible) return "noGerm";
      if (!this.hasLight || !this.hasSoil) return "sprout";
      return "grown";
    },
    applyRandomDisaster() {
      const rand = Math.random();
      if (rand < 0.6) return "lodging";
      if (rand < 0.8) return "pest";
      return "grown";
    },
    getFinalState() {
      const base = this.getBaseGrowthState();
      if (base === "grown") return this.applyRandomDisaster();
      return base;
    },
    getStateDetails() {
      const final = this.finalState;
      let title = "", desc = "", analysis = "";

      if (final === "noGerm") {
        title = "🌱 不发芽";
        desc = "种子沉睡，无法萌发。关键萌芽条件缺失！";
        analysis = "❌ 缺失必要发芽条件：空气、水分、适宜温度。";
      } else if (final === "sprout") {
        title = "🌿 发芽但不生长";
        desc = "幼芽萌发，但停滞在子叶期，无法继续长大。";
        analysis = "✅ 已发芽！但缺少光照/土壤，无法光合作用。";
      } else if (final === "grown") {
        title = "🍅 成功长大";
        desc = "番茄植株茁壮成长，开花结果，迎来丰收！";
        analysis = "🎉 环境完美且避开灾害，获得丰收！";
      } else if (final === "lodging") {
        title = "🌾 倒伏灾害";
        desc = "植株倒伏，茎秆折断，果实受损。";
        analysis = "⚠️ 环境完美，但遭遇60%概率大风倒伏！";
      } else if (final === "pest") {
        title = "🐛 虫害灾害";
        desc = "蚜虫/红蜘蛛爆发，植株衰弱无法结果。";
        analysis = "⚠️ 环境完美，但遭遇20%概率虫害！";
      }
      return { title, desc, analysis };
    },
    getEnvironmentDesc() {
      const parts = [];
      if (this.hasAir) parts.push("💨空气");
      if (this.hasWater) parts.push("💧水分");
      if (this.tempSuitable) parts.push("🌡️温度");
      if (this.hasLight) parts.push("☀️光照");
      if (this.hasSoil) parts.push("🪴土壤");
      return parts.join(" · ");
    },
    updateUI() {
      this.finalState = this.getFinalState();
      const { title, desc, analysis } = this.getStateDetails();
      this.stateTitle = title;
      this.stateDesc = desc;

      const env = this.getEnvironmentDesc();
      this.analysisHint = `📋 生长诊断：${analysis}<br><span style="font-size:0.85rem;">🔍 ${env}</span>`;

      if (this.getBaseGrowthState() !== "grown") {
        this.randomEventNote = `🎲 当前未达到完美条件，不会触发灾害。`;
      } else {
        this.randomEventNote = `⚠️ 环境完美！本次随机结果：倒伏60%、虫害20%、正常20%。`;
      }
    },
    setAllChecks(val) {
      this.hasAir = val;
      this.hasWater = val;
      this.tempSuitable = val;
      this.hasLight = val;
      this.hasSoil = val;
    },
    resetToDefault() {
      this.setAllChecks(true);
    },
  },
};
</script>

<style scoped>
.simulator-card {
  width: 100%;
  background: #fffef5;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  font-family: "Segoe UI", sans-serif;
}
.header h1 {
  font-size: 26px;
  color: #3c5e2c;
  margin: 0;
}
.header h1 span {
  font-size: 16px;
  color: #885a2b;
}
.header .sub {
  font-size: 14px;
  color: #666;
  margin-top: 6px;
}
.dashboard {
  display: flex;
  gap: 24px;
  margin-top: 20px;
  flex-wrap: wrap;
}
.factors-panel {
  flex: 1;
  min-width: 340px;
  background: #fcf8e8;
  border-radius: 16px;
  padding: 18px;
}
.factors-panel h2 {
  font-size: 18px;
  color: #6b4e1a;
  margin-bottom: 14px;
}
.factor-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.factor-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid #ddd;
}
.factor-label {
  font-weight: 600;
  color: #444;
}
.quick-buttons {
  display: flex;
  gap: 10px;
  margin-top: 16px;
  flex-wrap: wrap;
}
.quick-btn {
  padding: 8px 12px;
  border-radius: 10px;
  border: none;
  background: #e6f4dc;
  cursor: pointer;
  font-weight: 600;
}
.growth-panel {
  flex: 1.2;
  min-width: 360px;
  background: #fff9eb;
  border-radius: 16px;
  padding: 20px;
  text-align: center;
}
.growth-title {
  font-size: 18px;
  font-weight: bold;
  color: #9c4e1a;
  margin-bottom: 14px;
}
.illustration {
  display: flex;
  justify-content: center;
  margin: 10px 0;
}
.svg-container {
  width: 180px;
}
.state-message {
  margin: 10px 0;
}
.state-name {
  font-size: 18px;
  font-weight: bold;
  color: #b34e1a;
}
.state-detail {
  font-size: 14px;
  color: #555;
}
.condition-hint {
  background: #fff;
  padding: 12px;
  border-radius: 12px;
  font-size: 14px;
  text-align: left;
  margin: 10px 0;
}
.warning-badge {
  background: #fff4e0;
  padding: 10px;
  border-radius: 10px;
  font-size: 13px;
  color: #b85e1a;
}
footer {
  margin-top: 24px;
  font-size: 14px;
  color: #666;
  text-align: center;
  line-height: 1.5;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
  border-radius: 24px;
}
.slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}
input:checked + .slider {
  background-color: #4caf50;
}
input:checked + .slider:before {
  transform: translateX(20px);
}
</style>